import { useAuth } from "@assetwatch/auth";
import { toast } from "@assetwatch/ui";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useFeatureFlags } from "../../components/LaunchDarkly/useFeatureFlags";
import type {
  DetectedFeature,
  FeatureSeriesRequest,
  SaveFeedbackRequest,
} from "./api";
import {
  fetchCmeDay,
  fetchCmeFeatureDescriptions,
  fetchCmeFeatureSeries,
  saveCmeFeedback,
} from "./api";
import { detectedFeatureId } from "./lib";

/**
 * react-query hooks for the CME Feedback page.
 *
 * The rail is one ML API call for the selected day (`list_day`). A
 * `cme_user_id` in the URL is sent with that call and filters the items.
 * The chart is `feature_series`
 * for the opened monitoring point. Neither retries: a rejected parameter fails
 * the same way on a second attempt, and each Athena attempt is billed.
 */

/**
 * How long a loaded feature series counts as fresh. Each one is a billed
 * Athena scan, and switching between cards remounts the graph, which would
 * otherwise refetch it every time. New readings land about hourly.
 */
const FEATURE_SERIES_STALE_MS = 15 * 60 * 1000;

export function useCmeDay(
  date: string | undefined,
  cmeUserId: number | undefined,
) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: ["cmeFeedback", "day", date ?? null, cmeUserId ?? null],
    queryFn: () => {
      if (!date) {
        throw new Error("useCmeDay called with no date");
      }
      return fetchCmeDay({ user, flags, date, cmeUserId });
    },
    enabled: Boolean(date),
    retry: false,
  });
}

/** Every feature description, fetched once when Show Description is first opened. */
export function useCmeFeatureDescriptions(enabled: boolean) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: ["cmeFeedback", "featureDescriptions"],
    queryFn: () => fetchCmeFeatureDescriptions({ user, flags }),
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Writes the open machine's reviews, confirms with a toast, then reloads the
 * day so S3 is the source. A failure is shown inline by the form, not here.
 */
export function useSaveCmeFeedback(date: string | undefined) {
  const { user } = useAuth();
  const flags = useFeatureFlags();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (feedback: SaveFeedbackRequest) =>
      saveCmeFeedback({ user, flags, feedback }),
    onSuccess: async () => {
      toast.add("Saved.", { icon: true });
      if (!date) {
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: ["cmeFeedback", "day", date],
      });
    },
  });
}

/** One item's flex feature, ending on the selected day. */
export function useCmeFeatureSeries(
  item: FeatureSeriesRequest | undefined,
  days: number,
) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: [
      "cmeFeatureSeries",
      item?.id ?? null,
      item?.feature ?? null,
      item?.feature_group ?? null,
      item?.end_date ?? null,
      days,
    ],
    queryFn: () => {
      if (!item) {
        throw new Error("useCmeFeatureSeries called with no item");
      }
      return fetchCmeFeatureSeries({ user, flags, item, days });
    },
    enabled: Boolean(item),
    staleTime: FEATURE_SERIES_STALE_MS,
    retry: false,
  });
}

/**
 * One query per detected feature. Only the selected feature runs. Switching
 * back to a feature that already loaded uses the cached result.
 */
export function useCmeFeatureSeriesSet(
  asset: { id: string; monitoring_point_id: string },
  features: readonly DetectedFeature[],
  selectedIds: readonly string[],
  endDate: string,
  days: number,
) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQueries({
    queries: features.map((feature) => ({
      queryKey: [
        "cmeFeatureSeries",
        asset.id,
        feature.name,
        feature.feature_group,
        endDate,
        days,
      ],
      queryFn: () =>
        fetchCmeFeatureSeries({
          user,
          flags,
          item: {
            id: asset.id,
            monitoring_point_id: asset.monitoring_point_id,
            feature: feature.name,
            feature_group: feature.feature_group,
            end_date: endDate,
          },
          days,
        }),
      enabled: selectedIds.includes(detectedFeatureId(feature)),
      staleTime: FEATURE_SERIES_STALE_MS,
      retry: false,
    })),
  });
}
